import {
  Avatar,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as React from "react";
import * as yup from "yup";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useTeam, {
  addMemberToTeam,
  postTeamImage,
  putTeam,
  removeMemberFromTeam,
  updateMemberToTeam,
} from "hooks/useTeam";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  IRoleData,
  ITeamEdit,
  ITeamMember,
  MemberRoleEnum,
  MemberTeamManagementRoleEnum,
} from "utils/api.interfaces";
import { Transition } from "utils/common";

import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import { useQueryClient } from "@tanstack/react-query";
import DialogAddOpponent from "components/DialogAddOpponent";
import { nanoid } from "nanoid";
import AddTeamMember from "../components/AddTeamMember";
import ButtonArchiveTeam from "../components/ArchiveTeamButton";
import { ErrorMessageSlide } from "../components/ErrorMessageSlide";
import UploadImage from "../components/UploadImage";
import { useLeague } from "../hooks/useLeague";
import useOpponents from "../hooks/useOpponents";

interface TeamEditFormValues extends ITeamEdit {
  discriminator?: string;
}

interface MemberChanges {
  roles: IRoleData[];
  teamManagementRoles: MemberTeamManagementRoleEnum[];
}

export default function TeamEdit() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { dataTeamIsLoading, dataTeam, refetch } = useTeam(teamId, {
    refetchOnReconnect: "always",
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { dataOpponents } = useOpponents(teamId, {
    refetchOnReconnect: "always",
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const { dataLeague } = useLeague(dataTeam?.league?.id || "", {
    refetchOnReconnect: "always",
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const validationSchema = yup.object({
    discriminator: yup
      .string()
      .max(3, "Maximum of 3 uppercase characters.")
      .matches(/^[A-Z]+$/, "Maximum of 3 uppercase characters."),
  });

  const [newOpponentId, setNewOpponentId] = React.useState<string>("");
  const [showOpponentDialog, setShowOpponentDialog] =
    React.useState<boolean>(false);
  const [acceptedFiles, setAcceptedFiles] = React.useState<File[]>([]);
  const [newTeamUpdated, setNewTeamUpdated] = React.useState<string | boolean>(
    false
  );
  const [showSlideError, setShowSlideError] = React.useState<boolean>(false);
  const [showAdminSlideError, setShowAdminSlideError] =
    React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [memberChanges, setMemberChanges] = React.useState<
    Record<string, MemberChanges>
  >({});
  const [updatingMemberId, setUpdatingMemberId] = React.useState<string | null>(
    null
  );

  const initTeam: TeamEditFormValues = {
    id: dataTeam?.id || dataTeam?.teamId || "",
    name: dataTeam?.name || "",
    sportType: "Football",
    nameAbbreviation: dataTeam?.nameAbbreviation || "",
    seasonId: dataTeam?.season?.id || "",
    isGhost: dataTeam?.isGhost || false,
    isHistoric: dataTeam?.isHistoric || false,
    logoUrl: dataTeam?.logoUrl || "",
  };

  const formik = useFormik({
    initialValues: initTeam,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values: TeamEditFormValues) => {
      setIsLoading(true);
      try {
        if (acceptedFiles?.length > 0) {
          const res = await postTeamImage(acceptedFiles);
          await putTeam({ ...values, logoUrl: res.fileUrl });
        } else {
          await putTeam(values);
        }
        setNewTeamUpdated("Team successfully updated!");
      } catch (error) {
        setShowSlideError(true);
      } finally {
        setIsLoading(false);
      }
    },
  });

  React.useEffect(() => {
    queryClient.invalidateQueries(["opponent"]);
  }, [newOpponentId, queryClient]);

  React.useEffect(() => {
    if (!formik.isValid && formik.submitCount !== 0 && formik.isSubmitting) {
      window.scrollTo(0, 0);
    }
  }, [formik.submitCount, formik.isValid, formik.isSubmitting]);

  const handleRemoveMember = async (member: ITeamMember) => {
    if (
      window.confirm(
        `Are you sure you want to remove ${member.firstName} ${member.lastName} from the team?`
      )
    ) {
      try {
        await removeMemberFromTeam(teamId || "", member.id);
        setNewTeamUpdated("Member successfully removed!");
        refetch();
      } catch (error) {
        console.error("Failed to remove member:", error);
        setShowSlideError(true);
      }
    }
  };

  const getRolePriority = (roleType: MemberRoleEnum): number => {
    switch (roleType) {
      case MemberRoleEnum.player:
        return 0;
      case MemberRoleEnum.coach:
        return 1;
      case MemberRoleEnum.trainer:
        return 2;
      case MemberRoleEnum.assistant:
        return 3;
      case MemberRoleEnum.manager:
        return 4;
      case MemberRoleEnum.parent:
        return 5;
      case MemberRoleEnum.support:
        return 6;
      default:
        return 0;
    }
  };

  const handleRoleChange = React.useCallback(
    (memberId: string, role: MemberRoleEnum, checked: boolean) => {
      setMemberChanges((prev) => {
        const currentRoles = prev[memberId]?.roles || [];
        const currentManagementRoles =
          prev[memberId]?.teamManagementRoles || [];

        let newRoles = [...currentRoles];
        if (checked) {
          if (!newRoles.some((r) => r.roleType === role)) {
            newRoles.push({
              roleType: role,
              playerNumber: "0",
              foot: "undefined",
              firstPreferredPosition: undefined,
              secondPreferredPosition: undefined,
              thirdPreferredPosition: undefined,
              rolePriority: getRolePriority(role),
              isGuest: false,
            });
          }
        } else {
          newRoles = newRoles.filter((r) => r.roleType !== role);
        }

        return {
          ...prev,
          [memberId]: {
            ...prev[memberId],
            roles: newRoles,
            teamManagementRoles: currentManagementRoles,
          },
        };
      });
    },
    []
  );

  const handleManagementRoleChange = React.useCallback(
    (
      memberId: string,
      role: MemberTeamManagementRoleEnum,
      checked: boolean
    ) => {
      setMemberChanges((prev) => {
        const currentRoles = prev[memberId]?.roles || [];
        const currentManagementRoles =
          prev[memberId]?.teamManagementRoles || [];

        let newManagementRoles = [...currentManagementRoles];
        if (checked) {
          if (!newManagementRoles.includes(role)) {
            newManagementRoles.push(role);
          }
        } else {
          newManagementRoles = newManagementRoles.filter((r) => r !== role);
        }

        return {
          ...prev,
          [memberId]: {
            ...prev[memberId],
            roles: currentRoles,
            teamManagementRoles: newManagementRoles,
          },
        };
      });
    },
    []
  );

  // Initialize memberChanges when dataTeam changes
  React.useEffect(() => {
    if (dataTeam?.members) {
      const initialChanges = dataTeam.members.reduce((acc, member) => {
        acc[member.id] = {
          roles: member.rolesData.map((r) => ({
            roleType: r.roleType,
            playerNumber: r.playerNumber || "0",
            foot: r.foot || undefined,
            firstPreferredPosition: r.firstPreferredPosition || undefined,
            secondPreferredPosition: r.secondPreferredPosition || undefined,
            thirdPreferredPosition: r.thirdPreferredPosition || undefined,
            rolePriority: r.rolePriority || getRolePriority(r.roleType),
            isGuest: r.isGuest || false,
          })),
          teamManagementRoles: member.teamManagementRoles || [],
        };
        return acc;
      }, {} as Record<string, MemberChanges>);
      setMemberChanges(initialChanges);
    }
  }, [dataTeam?.members]);

  const handleUpdateMember = async (member: ITeamMember) => {
    if (!teamId || !memberChanges[member.id]) return;

    setUpdatingMemberId(member.id);
    try {
      await updateMemberToTeam(
        teamId,
        member.id,
        memberChanges[member.id].teamManagementRoles,
        memberChanges[member.id].roles
      );
      setNewTeamUpdated("Member roles successfully updated!");
      refetch();
    } catch (error) {
      console.error("Failed to update member:", error);
      setShowSlideError(true);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleAddMember = async (
    member: ITeamMember,
    roles: MemberRoleEnum[],
    isAdmin: boolean
  ) => {
    if (!teamId) return;

    try {
      await addMemberToTeam(teamId, member.id, roles, isAdmin);
      setNewTeamUpdated("Member successfully added!");
      refetch();
    } catch (error) {
      console.error("Failed to add member:", error);
      setShowSlideError(true);
    }
  };

  const renderMemberRow = React.useCallback(
    (member: ITeamMember) => (
      <TableRow
        key={member.id}
        sx={{
          "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
          "&:hover": { backgroundColor: "action.selected" },
          transition: "background-color 0.2s",
        }}
      >
        <TableCell sx={{ py: 1 }}>
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            component={member.ownerId ? Link : "div"}
            to={member.ownerId ? `/users/${member.ownerId}` : "#"}
            sx={{
              cursor: member.ownerId ? "pointer" : "default",
              color: member.ownerId ? "#000" : "gray",
            }}
          >
            <Avatar
              alt={`${member.firstName} ${member.lastName}`}
              src={member.imageUrl || member.profileImageUrl}
              sx={{ width: 20, height: 20 }}
            />
            <ListItemText
              primary={`${member.firstName} ${member.lastName}`}
              secondary={member.email || "no known email"}
              sx={{
                "& .MuiTypography-root": { fontSize: "0.875rem" },
                "& .MuiTypography-body2": { fontSize: "0.75rem" },
              }}
            />
          </Stack>
        </TableCell>
        <TableCell align="center" sx={{ py: 1 }}>
          <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}>
            {Object.values(MemberRoleEnum).map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      memberChanges[member.id]?.roles?.some(
                        (r) => r.roleType === role
                      ) ||
                      (!memberChanges[member.id] &&
                        member.rolesData.some((r) => r.roleType === role))
                    }
                    onChange={(e) => {
                      e.preventDefault();
                      handleRoleChange(member.id, role, e.target.checked);
                    }}
                  />
                }
                label={role}
                labelPlacement="end"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
            ))}
          </FormGroup>
        </TableCell>
        <TableCell align="center" sx={{ py: 1 }}>
          <FormGroup sx={{ flexDirection: "row", flexWrap: "wrap", gap: 0.5 }}>
            {Object.values(MemberTeamManagementRoleEnum).map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      memberChanges[member.id]?.teamManagementRoles?.includes(
                        role
                      ) ||
                      (!memberChanges[member.id] &&
                        member.teamManagementRoles?.includes(role))
                    }
                    onChange={(e) => {
                      e.preventDefault();
                      handleManagementRoleChange(
                        member.id,
                        role,
                        e.target.checked
                      );
                    }}
                  />
                }
                label={role}
                labelPlacement="end"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
            ))}
          </FormGroup>
        </TableCell>
        <TableCell align="center" sx={{ py: 1 }}>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => handleRemoveMember(member)}
              disabled={updatingMemberId === member.id}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Tooltip title="Save changes">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleUpdateMember(member)}
                  disabled={updatingMemberId === member.id}
                  color="primary"
                >
                  {updatingMemberId === member.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    ),
    [
      handleRoleChange,
      handleManagementRoleChange,
      memberChanges,
      updatingMemberId,
    ]
  );

  if (dataTeamIsLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Stack
        direction="row"
        justifyItems="center"
        alignItems="center"
        spacing={6}
      >
        <Typography
          component="h1"
          variant="h1"
          color="inherit"
          noWrap
          gutterBottom
          sx={{
            textAlign: "center",
            margin: "40px auto 40px",
          }}
        >
          Edit team
        </Typography>
        <ButtonArchiveTeam team={dataTeam} refetch={refetch} />
      </Stack>
      <form onSubmit={formik.handleSubmit}>
        <Stack
          spacing={3}
          sx={{
            margin: "auto",
            opacity: newTeamUpdated || isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? "none" : "auto",
          }}
        >
          <UploadImage maxFiles={1} onAcceptedFilesChange={setAcceptedFiles} />
          <TextField
            fullWidth
            id="id"
            name="id"
            label="id"
            type="text"
            disabled={true}
            value={formik.values.id}
            error={formik.touched.id && Boolean(formik.errors.id)}
            helperText={formik.touched.id && formik.errors.id}
          />
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Name"
            type="text"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            fullWidth
            id="nameAbbreviation"
            name="nameAbbreviation"
            label="Abbreviation"
            type="text"
            value={formik.values.nameAbbreviation}
            onChange={formik.handleChange}
            error={
              formik.touched.nameAbbreviation &&
              Boolean(formik.errors.nameAbbreviation)
            }
            helperText={
              formik.touched.nameAbbreviation && formik.errors.nameAbbreviation
            }
          />
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label" shrink={true}>
              SportType
            </InputLabel>
            <Select
              labelId="sportType"
              id="sportType"
              value={formik.values.sportType}
              label="sportType"
              onChange={formik.handleChange}
            >
              <MenuItem value="Football">Football</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{
              margin: " 20px auto",
            }}
            control={
              <Switch
                checked={formik.values.isGhost}
                inputProps={{ "aria-label": "controlled" }}
                value={formik.values.isGhost}
                onChange={(e) => {
                  e.target.checked
                    ? formik.setFieldValue("isGhost", true)
                    : formik.setFieldValue("isGhost", false);
                }}
              />
            }
            label="Team is a ghost team"
          />
          <FormControlLabel
            sx={{
              margin: " 20px auto",
            }}
            control={
              <Switch
                checked={formik.values.isHistoric}
                inputProps={{ "aria-label": "controlled" }}
                value={formik.values.isHistoric}
                onChange={(e) => {
                  e.target.checked
                    ? formik.setFieldValue("isHistoric", true)
                    : formik.setFieldValue("isHistoric", false);
                }}
              />
            }
            label="Team is a Historic team"
          />
          <TextField
            fullWidth
            id="leagueId"
            name="leagueId"
            label="leagueId"
            type="text"
            disabled={true}
            value={dataTeam?.league?.id}
          />
          <TextField
            fullWidth
            id="organizationId"
            name="organizationId"
            label="organizationId"
            type="text"
            disabled={true}
            value={dataTeam?.organization?.id}
          />

          {dataLeague && (
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label" shrink={true}>
                Season
              </InputLabel>
              <Select
                labelId="seasonId"
                id="seasonId"
                name="seasonId"
                value={dataTeam?.season?.id}
                label="seasonId"
                onChange={formik.handleChange}
              >
                {dataLeague?.seasons?.map((season) => (
                  <MenuItem key={nanoid(6)} value={season.id}>
                    {season.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {dataTeam && (
            <>
              <AddTeamMember
                teamId={teamId || ""}
                onMemberSelected={handleAddMember}
              />

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 1 }}>
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: "700",
                            marginBottom: "8px",
                            fontSize: "18px",
                            textAlign: "center",
                          }}
                        >
                          Set the roles & permissions
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 1 }}>Member</TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        Roles
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        Management Roles
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataTeam.members
                      .sort((a, b) => {
                        if (a.ownerId && !b.ownerId) return -1;
                        if (!a.ownerId && b.ownerId) return 1;
                        return `${a.firstName} ${a.lastName}`.localeCompare(
                          `${b.firstName} ${b.lastName}`
                        );
                      })
                      .map(renderMemberRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <Card>
            <CardContent>
              <Typography variant={"h3"} sx={{ marginBottom: "20px" }}>
                Opponents
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {dataOpponents?.opponents.map((opponent) => {
                  return (
                    <Button
                      color={"secondary"}
                      key={nanoid(6)}
                      sx={{
                        cursor: "pointer",
                        fontSize: "12px",
                        height: "42px",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                      }}
                      disabled={true}
                      size="small"
                      startIcon={
                        <Avatar
                          sx={{
                            backgroundColor: "#fff",
                            marginRight: "6px",
                            width: "35px",
                            height: "35px",
                          }}
                          src={opponent.logoUrl}
                        ></Avatar>
                      }
                      variant="outlined"
                    >
                      {opponent.name}
                    </Button>
                  );
                })}
              </Stack>
              <Button
                color="secondary"
                size={"small"}
                variant="outlined"
                onClick={() => setShowOpponentDialog(true)}
                sx={{ marginTop: "20px", marginLeft: "auto" }}
              >
                Add new opponent
              </Button>
            </CardContent>
          </Card>
          <Button
            color="secondary"
            variant="contained"
            type="submit"
            endIcon={
              isLoading && (
                <IconButton aria-label="" edge="end">
                  <CircularProgress
                    size={15}
                    sx={{ color: "#68F3CB" }}
                    color="success"
                  />
                </IconButton>
              )
            }
          >
            Update team
          </Button>
          <ErrorMessageSlide showError={showSlideError} />
          <ErrorMessageSlide
            showError={showAdminSlideError}
            errorMessage={"The team needs to have at least 1 Admin role"}
          />
        </Stack>

        <Dialog
          open={!!newTeamUpdated}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => {
            setNewTeamUpdated(false);
          }}
          aria-describedby="alert-dialog-slide-description"
          PaperProps={{
            sx: { backgroundColor: "#fff", minWidth: "400px" },
          }}
        >
          <DialogTitle variant="h4">{"Success!"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              {typeof newTeamUpdated === "string"
                ? newTeamUpdated
                : "Team successfully updated!"}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                setNewTeamUpdated(false);
              }}
            >
              close
            </Button>
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={() => navigate(`/teams/${dataTeam?.id}`)}
            >
              Go to team page
            </Button>
          </DialogActions>
        </Dialog>
      </form>
      <DialogAddOpponent
        team={dataTeam}
        isOpen={showOpponentDialog}
        onIsOpenChange={(value) => {
          setShowOpponentDialog(value);
        }}
        onIsOpponentAdded={(val, newOpponentId) => {
          setNewOpponentId(newOpponentId);

          setShowOpponentDialog(false);
        }}
      />
    </>
  );
}
